import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ArrowClockwise, StopCircle, WarningCircle } from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import { cn } from '~/lib/utils';
import { useCancelChatTask, useChatTaskProgress, useChatTasks } from '~/hooks/queries/use-chat-tasks';
import { DelegationProgressStrip } from './delegation-progress-strip';
import {
  agentStatusText,
  formatTaskClock,
  isSettledTaskStatus,
  openTaskIfVisible,
  taskChipView,
  type ChatTask,
  type ChatTaskAgent,
  type ChatTaskProgress,
  type TaskChipTone,
} from './chat-tasks-view';

/**
 * FRONT-3211: the composer's chip row — the `About: <project>` chip (passed
 * in as `leading`) and one chip per chat task: status dot, title, agent count
 * and a live timer, "needs you" or "done". A chip opens a small panel above
 * the composer with the task's agents, their live steps and "Stop task",
 * which stops only that task. The panel closes on Escape and on a click
 * outside it. One line: on a narrow screen the row scrolls sideways and
 * never widens the page.
 */

const CHIP_TONE: Record<TaskChipTone, string> = {
  working: 'border-primary/30 bg-primary/5',
  'needs-you': 'border-amber-500/50 bg-amber-500/10',
  waiting: 'border-border bg-background',
  done: 'border-success/40 bg-transparent',
  ended: 'border-border bg-transparent',
};

const DOT_TONE: Record<TaskChipTone, string> = {
  working: 'bg-primary motion-safe:animate-pulse [animation-duration:1.6s]',
  'needs-you': 'bg-amber-500',
  waiting: 'bg-muted-foreground/50',
  done: 'bg-success',
  ended: 'bg-destructive',
};

function agentDot(agent: ChatTaskAgent): string {
  switch (agent.status) {
    case 'running':
      return DOT_TONE.working;
    case 'needs_approval':
      return DOT_TONE['needs-you'];
    case 'waiting_to_start':
    case 'cancelled':
      return DOT_TONE.waiting;
    case 'done':
    case 'partial':
      return DOT_TONE.done;
    default:
      return DOT_TONE.ended;
  }
}

export interface ChatTaskChipsViewProps {
  tasks: readonly ChatTask[];
  progress: ChatTaskProgress;
  now: number;
  /** When this tab first saw each task delivered (see `taskChipView`). */
  firstSeenDelivered?: ReadonlyMap<string, number>;
  openTaskId: string | null;
  onToggle: (taskId: string) => void;
  onStop: (taskId: string) => void;
  stoppingTaskId?: string | null;
  /** The list could not be loaded (not "feature off": that reads as no tasks). */
  loadError?: boolean;
  onRetry?: () => void;
  /** The `About: <project>` chip, first in the row. */
  leading?: ReactNode;
  /** Test id of the row; the modal keeps `chat-scope-wrapper` on a filed chat. */
  rowTestId?: string;
  panelRef?: React.Ref<HTMLDivElement>;
}

/** Pure render of the row and the open panel; the container below owns data, clock and outside clicks. */
export function ChatTaskChipsView({
  tasks,
  progress,
  now,
  firstSeenDelivered,
  openTaskId,
  onToggle,
  onStop,
  stoppingTaskId,
  loadError,
  onRetry,
  leading,
  rowTestId = 'chat-task-row',
  panelRef,
}: ChatTaskChipsViewProps) {
  const chips = tasks
    .map((task) => ({ task, view: taskChipView(task, now, firstSeenDelivered?.get(task.id)) }))
    .filter((c) => c.view.visible);
  if (!leading && chips.length === 0 && !loadError) return null;
  const open = chips.find((c) => c.task.id === openTaskId)?.task ?? null;

  return (
    <div className="relative min-w-0 px-3 pb-2" data-testid={rowTestId}>
      {open && (
        <TaskPanel
          ref={panelRef}
          task={open}
          progress={progress}
          now={now}
          onStop={onStop}
          stopping={stoppingTaskId === open.id}
        />
      )}
      <div
        className="flex min-w-0 items-center gap-2 overflow-x-auto overscroll-x-contain py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-testid="composer-chip-row"
      >
        {leading}
        {chips.map(({ task, view }) => (
          <button
            key={task.id}
            type="button"
            data-testid="chat-task-chip"
            data-task-id={task.id}
            data-tone={view.tone}
            aria-expanded={open?.id === task.id}
            aria-controls={open?.id === task.id ? 'chat-task-panel' : undefined}
            aria-label={`${task.title}, ${view.agentCount}, ${view.isClock ? `working for ${view.status}` : view.status}. Show details`}
            onClick={() => onToggle(task.id)}
            className={cn(
              // 30px chip, 40px touch target via the pseudo-element (pointer-coarse).
              "relative inline-flex h-[30px] max-w-[280px] shrink-0 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium text-foreground",
              "transition-[background-color,border-color,scale] duration-150 ease-out active:scale-[0.96] motion-reduce:transition-none motion-reduce:active:scale-100",
              "after:absolute after:inset-x-0 after:-inset-y-[1px] after:content-[''] pointer-coarse:after:-inset-y-[5px]",
              'outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 hover:brightness-[0.98]',
              CHIP_TONE[view.tone],
              open?.id === task.id && 'ring-1 ring-primary/40',
            )}
          >
            <span aria-hidden className={cn('size-[7px] shrink-0 rounded-full', DOT_TONE[view.tone])} />
            <span className="min-w-0 truncate" data-testid="chat-task-chip-title">
              {task.title}
            </span>
            <span className="shrink-0 font-normal text-muted-foreground">{view.agentCount}</span>
            <span
              data-testid="chat-task-chip-status"
              className={cn(
                'shrink-0 font-normal tabular-nums',
                view.tone === 'needs-you' ? 'font-medium text-foreground' : 'text-muted-foreground',
              )}
            >
              {view.status}
            </span>
          </button>
        ))}
        {loadError && (
          <span
            role="alert"
            data-testid="chat-task-load-error"
            className="inline-flex h-[30px] shrink-0 items-center gap-1.5 rounded-md border border-destructive/30 bg-destructive/10 pl-2.5 pr-1 text-xs text-foreground"
          >
            <WarningCircle size={13} weight="fill" className="shrink-0 text-destructive" aria-hidden />
            Couldn't load this chat's jobs
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="relative ml-0.5 inline-flex h-6 items-center gap-1 rounded-[4px] px-1.5 font-medium transition-[background-color,scale] duration-150 hover:bg-destructive/10 active:scale-[0.96] motion-reduce:active:scale-100 after:absolute after:-inset-1 after:content-[''] pointer-coarse:after:-inset-2"
              >
                <ArrowClockwise size={12} aria-hidden />
                Retry
              </button>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

function TaskPanel({
  ref,
  task,
  progress,
  now,
  onStop,
  stopping,
}: {
  ref?: React.Ref<HTMLDivElement>;
  task: ChatTask;
  progress: ChatTaskProgress;
  now: number;
  onStop: (taskId: string) => void;
  stopping: boolean;
}) {
  const settled = isSettledTaskStatus(task.status);
  const started = formatTaskClock(task.startedAt ?? task.createdAt, now);
  return (
    <div
      ref={ref}
      id="chat-task-panel"
      role="dialog"
      aria-label={task.title}
      data-testid="chat-task-panel"
      className={cn(
        'absolute inset-x-3 bottom-full z-20 mb-1 max-h-80 overflow-y-auto rounded-xl border border-border bg-popover p-3 text-popover-foreground shadow-[0_1px_2px_rgba(0,0,0,0.06),0_8px_24px_-6px_rgba(0,0,0,0.16)]',
        'origin-bottom animate-in fade-in slide-in-from-bottom-1 zoom-in-[0.98] duration-150 ease-out motion-reduce:animate-none',
      )}
    >
      <h3 className="text-sm font-semibold leading-tight text-balance">{task.title}</h3>
      <p className="mt-0.5 text-xs text-pretty text-muted-foreground">
        {settled ? (
          'All agents are back. EDDY is writing one answer.'
        ) : (
          <>
            Started <span className="tabular-nums">{started}</span> ago · EDDY answers once all agents are back
          </>
        )}
      </p>
      <ul className="mt-2.5" aria-label="Agents in this job">
        {task.agents.map((agent) => {
          const steps = progress[agent.id];
          return (
            <li key={agent.id} data-testid="chat-task-agent" data-status={agent.status} className="border-t border-border py-2">
              <div className="flex items-center gap-2 text-[13px] font-medium">
                <span aria-hidden className={cn('size-[7px] shrink-0 rounded-full', agentDot(agent))} />
                <span className="min-w-0 truncate">{agent.agentLabel}</span>
                <span className="ml-auto shrink-0 text-xs font-normal tabular-nums text-muted-foreground">
                  {agentStatusText(agent, task, now)}
                </span>
              </div>
              {steps && agent.status === 'running' && (
                <DelegationProgressStrip
                  progress={steps}
                  expanded={false}
                  agentName={agent.agentLabel}
                  className="mt-1 border-t-0 px-0 py-0 pl-[15px]"
                />
              )}
            </li>
          );
        })}
      </ul>
      {!settled && (
        <div className="flex items-center justify-between gap-3 border-t border-border pt-2.5">
          <span className="text-xs text-muted-foreground">Stops only this job.</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="chat-task-stop"
            disabled={stopping}
            onClick={() => onStop(task.id)}
            className="active:scale-[0.96] motion-reduce:active:scale-100 pointer-coarse:h-10"
          >
            <StopCircle size={14} aria-hidden />
            {stopping ? 'Stopping…' : 'Stop job'}
          </Button>
        </div>
      )}
    </div>
  );
}

/** A clock that ticks once a second while `active`. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

/**
 * The container: loads the chat's tasks, keeps the clock, remembers when a
 * delivery was first seen, and closes the panel on Escape or an outside
 * click. Renders the scope chip alone when the chat has no tasks.
 */
export function ComposerChipRow({
  sessionId,
  leading,
  rowTestId,
  tasksEnabled = true,
}: {
  sessionId: string | null | undefined;
  leading?: ReactNode;
  rowTestId?: string;
  tasksEnabled?: boolean;
}) {
  const tasksQ = useChatTasks(sessionId, tasksEnabled);
  const { data: progress = {} } = useChatTaskProgress(sessionId);
  const cancel = useCancelChatTask(sessionId);
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  const firstSeen = useRef(new Map<string, number>());
  const panelRef = useRef<HTMLDivElement>(null);
  const tasks = tasksQ.data ?? [];
  for (const t of tasks) {
    if (t.deliveredAt && !firstSeen.current.has(t.id)) firstSeen.current.set(t.id, Date.now());
  }
  // Tick while any chip can still change on its own (a timer, a "done" about to leave).
  const canStillChange = (t: (typeof tasks)[number]): boolean => {
    const notYetDelivered = !t.deliveredAt;
    const doneAboutToLeave = Date.now() - (firstSeen.current.get(t.id) ?? 0) < 5_000;
    return notYetDelivered || doneAboutToLeave;
  };
  const ticking = tasks.some((t) => t.status !== 'cancelled' && canStillChange(t));
  const now = useNow(ticking);
  // The panel is open only while its chip shows. A stale id (the chip left
  // under an open panel) is dropped, so its Escape and outside-click
  // listeners go with the panel instead of swallowing the next Escape.
  const shownOpenId = openTaskIfVisible(tasks, openTaskId, now, firstSeen.current);
  useEffect(() => {
    if (openTaskId && !shownOpenId) setOpenTaskId(null);
  }, [openTaskId, shownOpenId]);

  useEffect(() => {
    if (!shownOpenId) return;
    const onPointer = (e: PointerEvent) => {
      const target = e.target as Element | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      if (target.closest?.('[data-testid="chat-task-chip"]')) return;
      setOpenTaskId(null);
    };
    // Capture phase, and the Escape stops there: it closes the panel, not the chat window around it.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpenTaskId(null);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [shownOpenId]);

  return (
    <ChatTaskChipsView
      tasks={tasks}
      progress={progress}
      now={now}
      firstSeenDelivered={firstSeen.current}
      openTaskId={shownOpenId}
      onToggle={(id) => setOpenTaskId((cur) => (cur === id ? null : id))}
      onStop={(id) => {
        setOpenTaskId(null);
        cancel.mutate(id);
      }}
      stoppingTaskId={cancel.isPending ? (cancel.variables ?? null) : null}
      loadError={tasksQ.isError}
      onRetry={() => void tasksQ.refetch()}
      leading={leading}
      rowTestId={rowTestId}
      panelRef={panelRef}
    />
  );
}
